import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  alpha,
  Avatar,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Timeline as TimelineIcon,
  AttachMoney as AttachMoneyIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';

import { 
  caixaFinanceiroService, 
  CaixaFinanceiro, 
  CaixaEstatisticas 
} from '../services/caixaFinanceiroService';
import { formatarValor } from '../utils/formatToView';

interface KpiCardProps {
  titulo: string;
  badge?: string;
  valor: string | number;
  subtituloLabel: string;
  subtituloValor: string | number;
  icone: React.ReactNode;
  cor: string;
}

const KpiCard: React.FC<KpiCardProps> = ({
  titulo,
  badge,
  valor,
  subtituloLabel,
  subtituloValor,
  icone,
  cor,
}) => {
  const theme = useTheme();

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 2.5,
        position: 'relative',
        overflow: 'hidden',
        border: `1px solid ${alpha(cor, 0.22)}`,
        background:
          theme.palette.mode === 'dark'
            ? `linear-gradient(145deg, ${alpha(cor, 0.08)} 0%, ${alpha('#1e1e2f', 0.95)} 100%)`
            : `linear-gradient(145deg, ${alpha(cor, 0.05)} 0%, #ffffff 100%)`,
        transition: 'all 0.25s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 20px -6px ${alpha(cor, 0.22)}`,
          borderColor: alpha(cor, 0.45),
        },
      }}
    >
      {/* Barra superior com a cor temática */}
      <Box sx={{ height: 3.5, width: '100%', bgcolor: cor }} />

      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        {/* Cabeçalho do Card */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.75}>
          <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{
                fontSize: '0.68rem',
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                color: 'text.secondary',
              }}
              noWrap
            >
              {titulo}
            </Typography>
            {badge && (
              <Chip
                label={badge}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.62rem',
                  fontWeight: 700,
                  bgcolor: alpha(cor, 0.12),
                  color: cor,
                  borderRadius: 1,
                  px: 0.25,
                }}
              />
            )}
          </Box>
          <Avatar
            sx={{
              bgcolor: alpha(cor, 0.12),
              color: cor,
              width: 32,
              height: 32,
              boxShadow: `0 2px 6px ${alpha(cor, 0.18)}`,
              '& .MuiSvgIcon-root': { fontSize: 18 },
            }}
          >
            {icone}
          </Avatar>
        </Box>

        {/* Valor Principal */}
        <Typography
          variant="h6"
          fontWeight={800}
          sx={{
            color: cor,
            fontSize: { xs: '1.05rem', sm: '1.2rem' },
            lineHeight: 1.2,
            mb: 1,
            letterSpacing: -0.2,
          }}
          noWrap
        >
          {valor}
        </Typography>

        {/* Pílula de Rodapé */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 0.5,
            px: 1,
            py: 0.45,
            borderRadius: 1.5,
            bgcolor: alpha(cor, 0.07),
            border: `1px solid ${alpha(cor, 0.14)}`,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.68rem',
              color: 'text.secondary',
              fontWeight: 500,
            }}
          >
            {subtituloLabel}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.72rem',
              fontWeight: 700,
              color: cor,
            }}
            noWrap
          >
            {subtituloValor}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

const CaixaFinanceiroDashboard: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const primario = theme.palette.primary.main;
  
  // Estados principais
  const [estatisticas, setEstatisticas] = useState<CaixaEstatisticas | null>(null);
  const [caixasRecentes, setCaixasRecentes] = useState<CaixaFinanceiro[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Carregar estatísticas e caixas em paralelo
      const [statsResponse, caixasResponse] = await Promise.all([
        caixaFinanceiroService.getEstatisticas(),
        caixaFinanceiroService.getCaixas({ page: 1, limit: 5, ativo: 'true' })
      ]);

      if (statsResponse) {
        console.log('📊 Debug statsResponse:', statsResponse);
        setEstatisticas(statsResponse.data);
      } else {
        console.warn('📊 Debug: statsResponse é null/undefined');
      }

      if (caixasResponse && caixasResponse.data) {
        console.log('📊 Debug caixasResponse:', caixasResponse);
        setCaixasRecentes(caixasResponse.data);
      } else {
        console.warn('📊 Debug: caixasResponse é null/undefined ou sem data');
      }

    } catch (err: any) {
      console.error('Erro ao carregar dashboard:', err);
      setError(err.message || 'Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Função auxiliar para converter valores para número de forma segura
  const safeNumber = (value: any): number => {
    if (value === null || value === undefined || value === '') {
      return 0;
    }
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };  

  const getTotalSaldoGeral = (): number => {
    if (!estatisticas?.caixasPorStatus || !Array.isArray(estatisticas.caixasPorStatus)) {
      console.log('📊 Debug getTotalSaldoGeral: Dados de estatísticas inválidos', {
        estatisticas,
        caixasPorStatus: estatisticas?.caixasPorStatus
      });
      return 0;
    }
    
    const total = estatisticas.caixasPorStatus.reduce((acc, status) => {
      const saldo = safeNumber(status.total_saldo);
      console.log(`📊 Debug saldo ${status.status}:`, status.total_saldo, '→', saldo);
      return acc + saldo;
    }, 0);
    
    console.log('📊 Debug getTotalSaldoGeral resultado:', total);
    return isNaN(total) ? 0 : total;
  };

  const getTotalMovimentacaoHoje = (): { entradas: number; saidas: number } => {
    if (!estatisticas?.movimentacaoHoje || !Array.isArray(estatisticas.movimentacaoHoje)) {
      console.log('📊 Debug getTotalMovimentacaoHoje: Dados de movimentação inválidos', {
        movimentacaoHoje: estatisticas?.movimentacaoHoje
      });
      return { entradas: 0, saidas: 0 };
    }
    
    const entradas = estatisticas.movimentacaoHoje
      .filter(mov => mov.tipo === 'entrada')
      .reduce((total, mov) => total + safeNumber(mov.total), 0);
    
    const saidas = estatisticas.movimentacaoHoje
      .filter(mov => mov.tipo === 'saida')
      .reduce((total, mov) => total + safeNumber(mov.total), 0);
    
    console.log('📊 Debug getTotalMovimentacaoHoje:', { entradas, saidas });
    return { 
      entradas: isNaN(entradas) ? 0 : entradas, 
      saidas: isNaN(saidas) ? 0 : saidas 
    };
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={loadDashboardData} startIcon={<RefreshIcon />}>
          Tentar Novamente
        </Button>
      </Box>
    );
  }

  const movimentacaoHoje = getTotalMovimentacaoHoje();
  const saldoGeral = getTotalSaldoGeral();

  return (
    <Box>

      {/* Cards de Resumo Modernizados */}
      <Grid container spacing={1.25} sx={{ mb: 2 }}>
        {/* Saldo Geral */}
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Saldo Total"
            valor={formatarValor(saldoGeral)}
            subtituloLabel="Status:"
            subtituloValor="Todos caixas ativos"
            icone={<AttachMoneyIcon />}
            cor={theme.palette.primary.main}
          />
        </Grid>

        {/* Entradas Hoje */}
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Entradas"
            badge="Hoje"
            valor={formatarValor(movimentacaoHoje.entradas)}
            subtituloLabel="Movimentações:"
            subtituloValor={`${estatisticas?.movimentacaoHoje?.filter(mov => mov.tipo === 'entrada')?.reduce((total, mov) => total + (mov.count || 0), 0) || 0} reg.`}
            icone={<TrendingUpIcon />}
            cor={theme.palette.success.main}
          />
        </Grid>

        {/* Saídas Hoje */}
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Saídas"
            badge="Hoje"
            valor={formatarValor(movimentacaoHoje.saidas)}
            subtituloLabel="Movimentações:"
            subtituloValor={`${estatisticas?.movimentacaoHoje?.filter(mov => mov.tipo === 'saida')?.reduce((total, mov) => total + (mov.count || 0), 0) || 0} reg.`}
            icone={<TrendingDownIcon />}
            cor={theme.palette.error.main}
          />
        </Grid>

        {/* Total de Caixas */}
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Total de Caixas"
            valor={estatisticas?.caixasPorStatus?.reduce((total, status) => total + (status.count || 0), 0) || 0}
            subtituloLabel="Abertos:"
            subtituloValor={`${estatisticas?.caixasPorStatus?.find(s => s.status === 'aberto')?.count || 0} caixas`}
            icone={<TimelineIcon />}
            cor={theme.palette.info.main}
          />
        </Grid>
      </Grid>

      {/* Caixas Recentes */}
      <Box sx={{ mt: 1 }}>
        <Card sx={{ borderRadius: 2, border: (t) => `1px solid ${alpha(t.palette.divider, 0.8)}`, boxShadow: 'none' }}>
            <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2">
                  💼 Caixas Ativos Recentes
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate('/caixa-financeiro?tab=gerenciar')}
                  sx={{ fontSize: '0.75rem', py: 0.25 }}
                >
                  Ver Todos
                </Button>
              </Box>
              
              <TableContainer
              component={Paper}
              elevation={2}
              sx={{
                borderRadius: 2,
                overflowX: 'auto',
                mb: 3,
                border: 'none'
              }}
              >
                <Table
                  size="small"
                  stickyHeader
                  sx={{
                    minWidth: 800,
                    '& .MuiTableBody-root .MuiTableCell-root': {
                      py: 0.6,
                      fontSize: '0.78rem'
                    }
                  }}
                >
                  <TableHead>
                    <TableRow sx={{
                      '& .MuiTableCell-head': {
                        bgcolor: (t) => `color-mix(in srgb, ${primario} 12%, ${t.palette.background.paper})`,
                        fontWeight: 700,
                        fontSize: '0.73rem',
                        py: 0.8,
                        borderBottom: `2px solid ${alpha(primario, 0.2)}`,
                      },
                    }}>
                      <TableCell>Nome</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Vinculação</TableCell>
                      <TableCell align="right">Saldo Atual</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {caixasRecentes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 1 }}>
                          <Typography color="text.secondary" variant="caption">
                            Nenhum caixa encontrado
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      caixasRecentes.map((caixa) => (
                        <TableRow key={caixa.id} hover sx={{ '&:hover': { bgcolor: alpha(primario, 0.03) }, '& .MuiTableCell-root': { py: 0.5, fontSize: '0.78rem' } }}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              {caixa.is_default && (
                                <Chip 
                                  label="DEFAULT" 
                                  size="small" 
                                  color="primary" 
                                  variant="outlined"
                                  sx={{ height: 16, fontSize: '0.625rem' }}
                                />
                              )}
                              <Typography fontWeight="medium" variant="body2">
                                {caixa.nome}
                              </Typography>
                            </Box>
                          </TableCell>
                          
                          <TableCell>
                            <Typography variant="caption">
                              {caixa.tipo_vinculo === 'escritorio' ? 'Escritório' :
                               caixa.tipo_vinculo === 'usuario' ? 'Usuário' : 'Default'}
                            </Typography>
                          </TableCell>
                          
                          <TableCell>
                            <Typography variant="caption">
                              {caixa.escritorio?.identificacao || 
                               caixa.usuario?.nome ||                               
                               'Sistema'}
                            </Typography>
                          </TableCell>
                          
                          <TableCell align="right">
                            <Typography 
                              fontWeight="bold" 
                              color={caixa.saldo_atual >= 0 ? 'success.main' : 'error.main'}
                              variant="body2"
                            >
                              {formatarValor(caixa.saldo_atual)}
                            </Typography>
                          </TableCell>
                          
                          <TableCell>
                            <Chip
                              label={caixa.status === 'aberto' ? 'Aberto' : 'Fechado'}
                              color={caixa.status === 'aberto' ? 'success' : 'error'}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.625rem' }}
                            />
                          </TableCell>
                          
                          <TableCell align="center">
                            <Button
                              size="small"
                              startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                              onClick={() => navigate(`/caixa-financeiro/${caixa.id}`)}
                              sx={{ fontSize: '0.75rem', py: 0.25 }}
                            >
                              Ver
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      </Box>
  );
};

export default CaixaFinanceiroDashboard;
